import { sortBy } from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import * as api from '../api/api';
import effLogo from '../assets/img/eff/logo.png';
import * as selectors from '../dff/dff-selectors';
import { typeSortMap } from '../dff/dff-state';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Toolbar } from '../toolbar/toolbar-component';

import './empty-folders.scss';
import { baseSelector } from '../store/selectors';
import { loadUser, selectMount } from '../dff/dff-slice';
import { MainMenuComponent } from '../menu/main-menu';

export interface EffOwnProps {
  history: any;
}

export const EmptyFolders: React.FC<EffOwnProps> = ({ history }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMounts, setIsLoadingMounts] = useState(true);
  const [mounts, setMounts] = useState([]);

  const user = useSelector(selectors.user);
  const mount = useSelector(selectors.mount);

  const dispatch = useDispatch();

  const loadMounts = useCallback(() => {
    fetch('/api/v2/mounts')
      .then(results => results.json())
      .then(data => {
        let mountsTemp = data.mounts;
        mountsTemp = mountsTemp.filter((mount: api.Mount) => mount.online && mount.origin !== 'desktop' && mount.permissions.WRITE);
        mountsTemp = sortBy(mountsTemp, (mount: api.Mount) => {
          return [typeSortMap[mount.type], -mount.isPrimary, mount.name];
        });
        setMounts(m => (m.concat(mountsTemp)));
        dispatch(selectMount(mountsTemp[0]));
        setIsLoadingMounts(false);
      });
  }, [dispatch]);

  useEffect(() => {
    document.title = 'Koofr EXP - Empty folder finder';
  }, []);

  useEffect(() => {
    fetch('/api/v2/user/appconfig')
    .then(results => {
      if (results.ok) {
        return results.json();
      }
      throw new Error(results.status as any);
    })
    .then(data => {
      dispatch(loadUser(data));
      setIsLoading(false);
    })
    .then(() => {
      loadMounts();
    })
    .catch(err => {
      console.error(err);
      setIsLoadingMounts(false);
      setIsLoading(false);
    });
  }, [dispatch, loadMounts]);

  const handleClick = () => {
    if (mount.id) {
      history.push(`/eff/${mount.id}?path=`);
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(selectMount(mounts.filter(mount => mount.id === e.target.value)[0]));
  };

  const mountName = (mount: api.Mount) => (
    <option
      value={mount.id}
      key={mount.id}
      disabled={mount.origin === 'desktop'}
      >
        { mount.name } { mount.type === 'import' ? `(shared by ${mount.owner.name})` : ''}
    </option>
  );

  const baseUrl = useSelector(baseSelector);

  return (
    <div className="eff">
        <Toolbar>
          <div className='l-toolbar__column'>
            <Link to="/">
              <img src={effLogo} alt="Koofr EFF logo" className="l-toolbar__logo"/>
            </Link>
            <div className='divider'></div>
            <div className='app-name'>Empty folder finder</div>
          </div>
          <div className='l-toolbar__column stretch'></div>
          <div className='l-toolbar__column'>
            { user ? 
              (
                <>
                  <div className='divider'></div>
                  <MainMenuComponent/>
                </>
              )
              : null
            }
          </div>
        </Toolbar>
        <div className="eff-container">
          <h1>Koofr empty folder finder</h1>
          <h2>Find and remove your empty folders.</h2>

          {
            !isLoading && !user ? (
              <a href={`/login?redirect=${encodeURIComponent("/eff/")}`} className="login-button">Sign in with Koofr</a>
            ) : null
          }

          {
            !isLoadingMounts && user && user.extra.subscription.isPayingUser && mounts ? (
              <div className="mount-selector">
                <select name="mountSelector" className="dropdown" value={mount.id} onChange={handleSelectChange}>
                  {
                    mounts.map((mount) => mountName(mount))
                  }
                </select>
                <button className="analyse-button" onClick={handleClick}>Go</button>
              </div>
            ) : null
          }

          {
            !isLoading && user && !user.extra.subscription.isPayingUser ? (
              <div className="upgrade">
                <p className="upgrade__text">Koofr empty folder finder is only available to paying users.</p>
                <a href={`${baseUrl}/app/account`} className="login-button">Upgrade your account</a>
              </div>
            ) : null
          }
          { isLoadingMounts ? (
            <LoadingSpinner />
          ) : null}
        </div>
    </div>
  );
};
